import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../apiClient";

// --- MUI Components ---
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Box,
  Link as MuiLink,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Tooltip, // Added Tooltip for better UX
} from "@mui/material";

// --- MUI Icons ---
// 1. ✅ Import VisibilityIcon for the View button
import {
  FileDownload as FileDownloadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export default function ApplicantList() {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedApplicantId, setSelectedApplicantId] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchApplicants = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const response = await apiClient.get("/careers/applicants");
      setApplicants(response.data);
    } catch (err) {
      console.error("Error fetching applicants:", err);
      let errorMsg = "Failed to fetch applicants. Please try again later.";
      if (
        err.response &&
        (err.response.status === 401 || err.response.status === 403)
      ) {
        errorMsg = "You are not authorized. Please log out and log in again.";
      }
      setPageError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplicants();
  }, [fetchApplicants]);

  const handleOpenDeleteDialog = (id) => {
    setSelectedApplicantId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedApplicantId(null);
    setOpenDeleteDialog(false);
  };

  const handleDelete = async () => {
    if (!selectedApplicantId) return;
    try {
      await apiClient.delete(`/careers/applicants/${selectedApplicantId}`);
      setApplicants((prev) =>
        prev.filter((app) => app._id !== selectedApplicantId)
      );
      setSnackbar({
        open: true,
        message: "Applicant deleted successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Error deleting applicant:", err);
      setSnackbar({
        open: true,
        message: "Failed to delete applicant.",
        severity: "error",
      });
    } finally {
      handleCloseDeleteDialog();
    }
  };


  // ✅ NEW SIMPLE DOWNLOAD HANDLER
  // This points directly to the backend route which forces the download
  const handleDownload = (resumeUrl) => {
    if (!resumeUrl) return;

    // 1. Get the filename (e.g., "resume-123.pdf")
    const fileName = resumeUrl.split('/').pop();

    // 2. Point to the API Download Route
    const downloadEndpoint = `https://api.horizon.co.zm/api/download/resume/${fileName}`;

    // 3. Go to the URL (Browser handles the rest)
    window.location.href = downloadEndpoint;
  };



  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };


  

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (pageError) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {pageError}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          pb: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ width: 160, minWidth: 160 }} />
        <Typography
          variant="h4"
          component="h1"
          sx={{ flexGrow: 1, textAlign: "center", fontWeight: "600" }}
        >
          View Applicants
        </Typography>
        <Box sx={{ width: 160, minWidth: 160 }} />
      </Box>

      {/* Data Table */}
      <TableContainer
        component={Paper}
        sx={{ boxShadow: "0 4px 12px rgba(0,0,0,0.05)", borderRadius: "10px" }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="applicants table">
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  fontWeight: "600",
                  backgroundColor: "#f9fafb",
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  py: 1,
                },
              }}
            >
              <TableCell>Date</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Job Title</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applicants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  No applicants found.
                </TableCell>
              </TableRow>
            ) : (
              applicants.map((app) => (
                <TableRow
                  key={app._id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "#f9fafb" },
                    td: { verticalAlign: "middle" },
                  }}
                >
                  <TableCell sx={{ color: "text.secondary" }}>
                    {formatDate(app.appliedAt)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{app.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={app.jobTitle}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <MuiLink href={`mailto:${app.email}`} underline="hover">
                      {app.email}
                    </MuiLink>
                  </TableCell>
                  <TableCell>
                    <MuiLink href={`tel:${app.phone}`} underline="hover">
                      {app.phone}
                    </MuiLink>
                  </TableCell>

                  {/* --- ACTIONS COLUMN --- */}
                  <TableCell align="right">
                    {/* 2. ✅ VIEW BUTTON */}
                    <Tooltip title="View Resume">
                      <Button
                        variant="outlined"
                        size="small"
                        color="info" // Blue color for View
                        startIcon={<VisibilityIcon />}
                        href={app.resumeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ mr: 1 }}
                      >
                        View
                      </Button>
                    </Tooltip>

                    {/* DOWNLOAD BUTTON */}
                    <Tooltip title="Download Resume">
                      <Button
                        variant="outlined"
                        size="small"
                        color="success"
                        startIcon={<FileDownloadIcon />}
                        // ✅ CHANGED: Use onClick instead of href
                        onClick={() =>
                          handleDownload(
                            app.resumeUrl,
                            `Resume-${app.name}.pdf`
                          )
                        }
                        sx={{ mr: 1 }}
                      >
                        Download
                      </Button>
                    </Tooltip>

                    {/* DELETE BUTTON */}
                    <Tooltip title="Delete Application">
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteDialog(app._id)}
                        aria-label="delete applicant"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this application? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
